/*
 * Table Menu - Floating toolbar shown while the cursor is inside a table.
 * Anchored to the table itself (not the text selection) so it doesn't
 * collide with the text formatting bubble.
 */

import { Button, Separator } from '@blogging/ui'
import type { Editor } from '@tiptap/core'
import { findParentNode } from '@tiptap/core'
import { useCurrentEditor, useEditorState } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import {
  BetweenHorizontalEnd,
  BetweenHorizontalStart,
  BetweenVerticalEnd,
  BetweenVerticalStart,
  Columns3,
  PanelLeft,
  PanelTop,
  Rows3,
  TableCellsMerge,
  TableCellsSplit,
  Trash2,
  type LucideIcon,
} from 'lucide-react'

const findTableElement = (editor: Editor): HTMLElement | null => {
  const table = findParentNode((node) => node.type.name === 'table')(
    editor.state.selection,
  )
  if (!table) return null
  const dom = editor.view.nodeDOM(table.pos)
  return dom instanceof HTMLElement ? dom : null
}

interface TableAction {
  name: string
  icon: LucideIcon
  command: (editor: Editor) => boolean
  isEnabled?: boolean
  destructive?: boolean
}

export const TableMenu = () => {
  const { editor } = useCurrentEditor()

  const state = useEditorState({
    editor,
    selector: ({ editor }) =>
      editor
        ? {
            canMerge: editor.can().mergeCells(),
            canSplit: editor.can().splitCell(),
          }
        : null,
  })

  if (!editor) return null

  const groups: TableAction[][] = [
    [
      {
        name: 'Add row above',
        icon: BetweenHorizontalStart,
        command: (e) => e.chain().focus().addRowBefore().run(),
      },
      {
        name: 'Add row below',
        icon: BetweenHorizontalEnd,
        command: (e) => e.chain().focus().addRowAfter().run(),
      },
      {
        name: 'Delete row',
        icon: Rows3,
        command: (e) => e.chain().focus().deleteRow().run(),
        destructive: true,
      },
    ],
    [
      {
        name: 'Add column before',
        icon: BetweenVerticalStart,
        command: (e) => e.chain().focus().addColumnBefore().run(),
      },
      {
        name: 'Add column after',
        icon: BetweenVerticalEnd,
        command: (e) => e.chain().focus().addColumnAfter().run(),
      },
      {
        name: 'Delete column',
        icon: Columns3,
        command: (e) => e.chain().focus().deleteColumn().run(),
        destructive: true,
      },
    ],
    [
      {
        name: 'Toggle header row',
        icon: PanelTop,
        command: (e) => e.chain().focus().toggleHeaderRow().run(),
      },
      {
        name: 'Toggle header column',
        icon: PanelLeft,
        command: (e) => e.chain().focus().toggleHeaderColumn().run(),
      },
      {
        name: 'Merge cells',
        icon: TableCellsMerge,
        command: (e) => e.chain().focus().mergeCells().run(),
        isEnabled: state?.canMerge,
      },
      {
        name: 'Split cell',
        icon: TableCellsSplit,
        command: (e) => e.chain().focus().splitCell().run(),
        isEnabled: state?.canSplit,
      },
    ],
    [
      {
        name: 'Delete table',
        icon: Trash2,
        command: (e) => e.chain().focus().deleteTable().run(),
        destructive: true,
      },
    ],
  ]

  return (
    <BubbleMenu
      editor={editor}
      pluginKey="table-menu"
      updateDelay={0}
      shouldShow={({ editor }) => editor.isEditable && editor.isActive('table')}
      getReferencedVirtualElement={() => {
        const table = findTableElement(editor)
        if (!table) return null
        return {
          getBoundingClientRect: () => table.getBoundingClientRect(),
          getClientRects: () => table.getClientRects(),
        }
      }}
      options={{
        placement: 'bottom-start',
        offset: 8,
        flip: true,
        shift: true,
      }}
    >
      <div className="flex w-fit max-w-[90vw] items-center overflow-x-auto rounded-md border border-muted bg-background shadow-xl">
        {groups.map((group, index) => (
          <div key={index} className="flex items-center">
            {index > 0 && <Separator orientation="vertical" className="h-6" />}
            {group.map((action) => (
              <Button
                key={action.name}
                type="button"
                size="sm"
                variant="ghost"
                className="rounded-none"
                title={action.name}
                aria-label={action.name}
                disabled={action.isEnabled === false}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => action.command(editor)}
              >
                <action.icon
                  className={
                    action.destructive ? 'h-4 w-4 text-destructive' : 'h-4 w-4'
                  }
                />
              </Button>
            ))}
          </div>
        ))}
      </div>
    </BubbleMenu>
  )
}

export default TableMenu
